import React from 'react';
import { Search, Music, Info, CheckCircle, ArrowRight, ArrowLeft, AlertTriangle } from 'lucide-react';
import FormCard from '../../components/ui/FormCard';
import RoutineFormModal from '../../components/routines_user/RoutineFormModal';
import AvailableRoutineCard from './AvailableRoutineCard';
import SelectedRoutineCard from './SelectedRoutineCard';
import TypeChangeModal from './TypeChangeModal';
import GlowingCard from '../ui/GlowingCard';

import { formatCurrency } from '../../utils/competitionUtils';
import { CompetitionStepViewProps } from '../../types/competition';
import { Routine } from '../../types/registration';

// Available Routines content component to be reused in both dark and light modes
const AvailableRoutinesContent: React.FC<{
  filteredRoutines: Routine[];
  searchQuery: string;
  isRoutineSelected: (routine: Routine) => boolean;
  toggleRoutineSelection: (routine: Routine) => void;
  toggleAudio: (routine: Routine) => void;
  currentlyPlaying: string | null;
  routineBeingAdded: string | null;
  isDarkMode: boolean;
}> = ({ 
  filteredRoutines, 
  searchQuery, 
  isRoutineSelected, 
  toggleRoutineSelection, 
  toggleAudio, 
  currentlyPlaying, 
  routineBeingAdded, 
  isDarkMode 
}) => (
  <div className="space-y-3">
    {filteredRoutines.length === 0 ? (
      <div className={`p-4 rounded-lg border text-center ${
        isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'
      }`}>
        {searchQuery ? 'No routines match your search' : 'No routines available'}
      </div>
    ) : (
      filteredRoutines.map(routine => (
        <AvailableRoutineCard
          key={routine.routineId}
          routine={routine}
          isSelected={isRoutineSelected(routine)}
          toggleRoutineSelection={toggleRoutineSelection}
          toggleAudio={(audioUrl: string) => {
            const routineWithUrl = { ...routine, songFileUrl: audioUrl } as Routine;
            toggleAudio(routineWithUrl);
          }}
          currentlyPlaying={currentlyPlaying}
          isDarkMode={isDarkMode}
          isAddingRoutine={routineBeingAdded === routine.routineId}
          onToggleSelection={toggleRoutineSelection}
          onToggleAudio={(routine: Routine) => {
            toggleAudio(routine);
          }}
        />
      ))
    )}
  </div>
);

// Selected Routines content component to be reused in both dark and light modes
const SelectedRoutinesContent: React.FC<{
  selectedRoutines: any[];
  routineStyles: any[];
  competitionCategories: any[];
  updateRoutineOptions: (routineId: string, options: any) => void;
  handleEditRoutine: (routine: any) => void;
  toggleRoutineSelection: (routine: Routine) => void;
  calculateRoutinePrice: (routine: any) => number;
  toggleAudio: (audioUrl: string) => void;
  currentlyPlaying: string | null;
  isDarkMode: boolean;
}> = ({ 
  selectedRoutines, 
  routineStyles, 
  competitionCategories, 
  updateRoutineOptions, 
  handleEditRoutine, 
  toggleRoutineSelection, 
  calculateRoutinePrice, 
  toggleAudio, 
  currentlyPlaying, 
  isDarkMode 
}) => (
  <div className="space-y-4">
    {selectedRoutines.length === 0 ? (
      <div className={`p-4 rounded-lg border text-center ${
        isDarkMode ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-500'
      }`}>
        No routines selected yet
      </div>
    ) : (
      selectedRoutines.map(routine => (
        <SelectedRoutineCard
          key={routine.routineCloneId || routine.routineId}
          routine={routine}
          routineStyles={routineStyles}
          competitionCategories={competitionCategories}
          updateRoutineOptions={updateRoutineOptions}
          handleEditRoutine={handleEditRoutine}
          toggleRoutineSelection={toggleRoutineSelection}
          calculateRoutinePrice={calculateRoutinePrice}
          toggleAudio={(audioUrl: string) => toggleAudio(audioUrl)}
          currentlyPlaying={currentlyPlaying}
          isDarkMode={isDarkMode}
        />
      ))
    )}
  </div>
);

/**
 * View component for the competition step
 * Updated to work with immutable clone-on-register approach
 */
const CompetitionStepView: React.FC<CompetitionStepViewProps> = ({
  event,
  loading,
  error,
  routines,
  selectedRoutines,
  routineStyles,
  competitionCategories = [],
  competitionPricingRules,
  ageDivisions = [],
  searchQuery,
  onSearchChange,
  toggleRoutineSelection,
  updateRoutineOptions,
  toggleAudio,
  currentlyPlaying,
  handleEditRoutine,
  calculateRoutinePrice,
  calculateTotalPrice,
  onNext,
  onBack,
  isDarkMode,
  
  // Edit modal props
  editingRoutine,
  isEditModalOpen,
  onCloseEditModal,
  onUpdateRoutine,
  onRoutineUpdateComplete,
  
  // Type change modal props
  showTypeChangeModal,
  typeChangeInfo,
  onConfirmTypeChange,
  onCancelTypeChange,

  // Pass mappings from the container
  routineMappings = {},
  
  // Loading state for routine addition
  routineBeingAdded = null,
  
  // Navigation prop
  renderNavigation
}) => {
  // Don't display competition step if this event doesn't include a competition
  if (!event || !event.type || !event.type.includes('Competition')) {
    return (
      <div className="p-4 md:p-6">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mb-6">
          <p>This event does not include a competition component.</p>
        </div>
        
        <div className="flex justify-between mt-8">
          <button
            onClick={onBack}
            className={`px-4 py-2 rounded-lg ${
              isDarkMode
                ? 'bg-gray-800 hover:bg-gray-700 text-white'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
            }`}
          >
            Back
          </button>
          
          <button
            onClick={onNext}
            className={`px-4 py-2 rounded-lg ${
              isDarkMode
                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    );
  }

  // Show loading indicator while data is being fetched
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show error message if there was an error loading data
  if (error) {
    return (
      <div className={`p-4 rounded-lg ${
        isDarkMode ? 'bg-red-500/10 text-red-400' : 'bg-red-100 text-red-800'
      }`}>
        <p>Error loading routines: {error}</p>
      </div>
    );
  }

  // Check if all selected routines have styles
  const allRoutinesHaveStyles = selectedRoutines.every(routine => routine.style);

  // Calculate total price
  const totalPrice = calculateTotalPrice();
  const formattedTotalPrice = formatCurrency(totalPrice);

  // Filter routines based on search query
  const filteredRoutines = routines.filter(routine => {
    const title = (routine.title || routine.name || '').toLowerCase();
    const searchLower = searchQuery.toLowerCase();
    return title.includes(searchLower);
  });

  // Check which routines are already selected
  const isRoutineSelected = (routine: Routine) => {
    return selectedRoutines.some(selected => 
      selected.routineId === routine.routineId || 
      selected.originalRoutineId === routine.routineId
    );
  };

  return (
    <div className="h-full w-full flex flex-col">
      {/* Fixed Header */}
      <div className={`flex-shrink-0 p-4 md:p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 md:gap-4">
          <div className="flex-shrink-0">
            <h2 className={`responsive-heading font-bold mb-2 flex items-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <Music className="h-6 w-6 mr-2 text-blue-500" />
              Select Routines
            </h2>
            <p className={`${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              Select the routines you want to register for this competition
            </p>
          </div>
          
          {/* Information Alert */}
          <div className={`flex items-center p-2 px-3 rounded-lg border flex-shrink-0 md:max-w-xl lg:max-w-2xl xl:max-w-3xl ${
            isDarkMode 
              ? 'border-blue-800 text-blue-300 bg-blue-900/10' 
              : 'border-blue-200 text-blue-800 bg-blue-50'
          }`}>
            <Info className="h-5 w-5 mr-2 flex-shrink-0" />
            <p className="text-sm">
              To create new routines, please visit the Routines page in your dashboard first.
            </p>
          </div>
        </div>
      </div>
      
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <div className="space-y-8">
          {/* Global warning if no competition settings configured */}
          {(routineStyles.length === 0 || competitionPricingRules?.length === 0) && (
            <div className={`p-4 rounded-lg border ${
              isDarkMode ? 'bg-yellow-500/10 border-yellow-500/30 text-yellow-300' : 'bg-yellow-50 border-yellow-200 text-yellow-700'
            }`}>
              <div className="flex">
                <AlertTriangle className="h-5 w-5 mr-2 flex-shrink-0" />
                <div>
                  <h3 className="font-medium">Missing Competition Configuration</h3>
                  <p className="mt-1">
                    {routineStyles.length === 0 && competitionPricingRules?.length === 0 ? (
                      "This event does not have any routine categories or pricing types configured."
                    ) : routineStyles.length === 0 ? (
                      "This event does not have any routine categories configured."
                    ) : (
                      "This event does not have any routine pricing types configured."
                    )}
                    {" "}Please contact the event organizer to set up the proper registration options before proceeding.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Search and Selected Count */}
          <div className="registration-flow__form-row flex flex-col md:flex-row items-stretch md:items-center justify-between mb-6 gap-4">
            {/* Search Box */}
            <div className={`relative flex-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              <input
                type="text"
                className={`form-input pr-10 pl-4 py-2 w-full rounded-lg border ${
                  isDarkMode 
                    ? 'bg-gray-800 border-gray-700 text-gray-200 focus:border-blue-500' 
                    : 'bg-white border-gray-300 text-gray-900 focus:border-blue-500'
                } focus:ring-1 focus:ring-blue-500 focus:outline-none`}
                placeholder="Search routines..."
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
            </div>
            
            {/* Selected Count */}
            <div className={`px-4 py-2 rounded-lg border ${
              isDarkMode 
                ? 'border-blue-800 text-blue-400' 
                : 'border-blue-200 text-blue-700'
            }`}>
              <span className="font-medium">{selectedRoutines.length}</span>&nbsp;{selectedRoutines.length === 1 ? 'routine' : 'routines'} selected
            </div>
          </div>
          
          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Available Routines */}
            <div>
              <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                Available Routines
              </h3>
              
              {isDarkMode ? (
                <GlowingCard
                  glowColor="from-blue-600/10 via-purple-600/10 to-violet-600/10"
                  glowIntensity="blur-xl"
                  cardOpacity="bg-[#1a1f2d]/90"
                  className="p-4 max-h-96 overflow-y-auto"
                >
                  <AvailableRoutinesContent 
                    filteredRoutines={filteredRoutines}
                    searchQuery={searchQuery}
                    isRoutineSelected={isRoutineSelected}
                    toggleRoutineSelection={toggleRoutineSelection}
                    toggleAudio={(routine: Routine) => {
                      if (routine.songFileUrl) {
                        toggleAudio(routine.songFileUrl);
                      }
                    }}
                    currentlyPlaying={currentlyPlaying}
                    routineBeingAdded={routineBeingAdded}
                    isDarkMode={isDarkMode}
                  />
                </GlowingCard>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-4 max-h-96 overflow-y-auto">
                  <AvailableRoutinesContent 
                    filteredRoutines={filteredRoutines}
                    searchQuery={searchQuery}
                    isRoutineSelected={isRoutineSelected}
                    toggleRoutineSelection={toggleRoutineSelection}
                    toggleAudio={(routine: Routine) => {
                      if (routine.songFileUrl) {
                        toggleAudio(routine.songFileUrl);
                      }
                    }}
                    currentlyPlaying={currentlyPlaying}
                    routineBeingAdded={routineBeingAdded}
                    isDarkMode={isDarkMode}
                  />
                </div>
              )}
            </div>
            
            {/* Selected Routines */}
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Selected Routines
                </h3>
                
                <div className={`text-sm font-medium ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                  Total: {formattedTotalPrice}
                </div>
              </div>
              
              {isDarkMode ? (
                <GlowingCard
                  glowColor="from-green-600/10 via-blue-600/10 to-purple-600/10"
                  glowIntensity="blur-xl"
                  cardOpacity="bg-[#1a1f2d]/90"
                  className="p-4 max-h-96 overflow-y-auto"
                >
                  <SelectedRoutinesContent 
                    selectedRoutines={selectedRoutines}
                    routineStyles={routineStyles}
                    competitionCategories={competitionCategories}

                    updateRoutineOptions={updateRoutineOptions}
                    handleEditRoutine={handleEditRoutine}
                    toggleRoutineSelection={toggleRoutineSelection}
                    calculateRoutinePrice={calculateRoutinePrice}
                    toggleAudio={toggleAudio}
                    currentlyPlaying={currentlyPlaying}
                    isDarkMode={isDarkMode}
                  />
                </GlowingCard>
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-4 max-h-96 overflow-y-auto">
                  <SelectedRoutinesContent 
                    selectedRoutines={selectedRoutines}
                    routineStyles={routineStyles}
                    competitionCategories={competitionCategories}

                    updateRoutineOptions={updateRoutineOptions}
                    handleEditRoutine={handleEditRoutine}
                    toggleRoutineSelection={toggleRoutineSelection}
                    calculateRoutinePrice={calculateRoutinePrice}
                    toggleAudio={toggleAudio}
                    currentlyPlaying={currentlyPlaying}
                    isDarkMode={isDarkMode}
                  />
                </div>
              )}
            </div>
          </div>
          
        </div>
      </div>


      {/* Edit Routine Modal */}
      {isEditModalOpen && editingRoutine && (
        <RoutineFormModal
          routine={editingRoutine}
          isOpen={isEditModalOpen}
          onClose={onCloseEditModal}
          onSubmit={(updatedRoutine: any) => {
            onUpdateRoutine(editingRoutine, updatedRoutine);
          }}
          onComplete={onRoutineUpdateComplete}
          isDarkMode={isDarkMode}
        />
      )}
      
      {/* Type Change Confirmation Modal */}
      <TypeChangeModal
        show={showTypeChangeModal}
        typeChangeInfo={typeChangeInfo}
        onConfirm={onConfirmTypeChange}
        onCancel={onCancelTypeChange}
        isDarkMode={isDarkMode}
      />

      {/* Render standardized navigation */}
      {renderNavigation && renderNavigation(
        onNext,
        onBack,
        selectedRoutines.length === 0 ? false : selectedRoutines.every((routine: Routine) => 
          routine.style || (routineMappings[routine.routineId || '']?.style) &&
          routine.category || (routineMappings[routine.routineId || '']?.category)
        ),
        "Continue"
      )}
    </div>
  );
};

export default CompetitionStepView;
